<template>
  <div class="ad-page">
    <div class="ad-toolbar">
      <div class="ad-search">
        <span class="mdi mdi-magnify"></span>
        <input v-model="search" class="ad-input" placeholder="Buscar parque…" aria-label="Buscar parque" />
      </div>
      <span class="ad-toolbar__spacer"></span>
      <button type="button" class="ad-btn ad-btn--primary" @click="panel = { mode: 'create' }">＋ Nuevo parque</button>
    </div>

    <div class="ad-card">
      <p v-if="loading && !parks.length" class="ad-empty">Cargando parques…</p>
      <p v-else-if="loadError" class="ad-empty">{{ loadError }}</p>
      <p v-else-if="!parks.length" class="ad-empty">{{ search ? 'Ningún parque coincide con la búsqueda.' : 'Aún no hay parques. Crea el primero con "＋ Nuevo parque".' }}</p>
      <div v-else class="ad-table-wrap">
        <table class="ad-table">
          <thead>
            <tr>
              <th>Parque</th><th>Plan</th><th>Inicio</th><th>Próximo cobro</th><th>Usuarios activos</th><th>Último acceso</th><th>Estatus</th><th class="ad-col-actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in parks" :key="p.id">
              <td>
                <div class="ad-cell-name">{{ p.name }}</div>
                <div v-if="p.city || p.state" class="ad-cell-sub">{{ [p.city, p.state].filter(Boolean).join(', ') }}</div>
              </td>
              <td><span class="ad-pill ad-pill--gray">{{ p.plan || 'Sin plan' }}</span></td>
              <td>{{ formatDate(p.subscriptionStartedAt) }}</td>
              <td>
                <template v-if="p.nextBillingAt">{{ formatDate(p.nextBillingAt) }} <span class="ad-mono">{{ money(p.nextBillingAmount) }}</span></template>
                <template v-else>—</template>
              </td>
              <td>
                {{ p.activeUsers }}
                <span v-if="p.pendingUsers" class="ad-cell-sub" style="display:inline"> · {{ p.pendingUsers }} sin activar</span>
              </td>
              <td>{{ relativeTime(p.lastAccessAt) }}</td>
              <td>
                <span class="ad-pill" :class="PARK_STATUS[p.status].cls" :title="p.status === 'SUSPENDED' ? p.suspendedReason || '' : ''">
                  {{ PARK_STATUS[p.status].label }}
                </span>
              </td>
              <td class="ad-col-actions"><RowMenu :items="menuFor(p)" @select="(key) => onAction(key, p)" /></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <ParkFormPanel v-if="panel" :park="panel.mode === 'edit' ? panel.park : null" @close="panel = null" @done="onPanelDone" />
    <ImpersonateModal v-if="modal?.type === 'impersonate'" :park="modal.park" @close="modal = null" />
    <ReasonModal
      v-if="modal?.type === 'suspend'"
      :title="`Suspender acceso · ${modal.park.name}`"
      message="Todos los usuarios e inquilinos de este parque perderán el acceso y se cerrarán sus sesiones."
      confirm-label="Suspender acceso"
      :action="(reason) => suspendPark(modal.park.id, reason)"
      @close="modal = null"
      @done="afterAction('Acceso suspendido')"
    />
    <AddAdminModal v-if="modal?.type === 'admin'" :park="modal.park" @close="modal = null" @done="onAdminAdded" />
    <InviteLinkModal
      v-if="modal?.type === 'invite'"
      title="Invitación del nuevo administrador"
      intro="Se envió la invitación por correo. Si no le llega, comparte este link para que cree su contraseña."
      :url="modal.inviteUrl"
      :expires-at="modal.expiresAt"
      @close="modal = null"
    />
    <DeleteParkModal v-if="modal?.type === 'delete'" :park="modal.park" @close="modal = null" @done="afterAction('Parque eliminado')" />
    <div v-if="toast" class="ad-toast" role="status">{{ toast }}</div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { listParks, reactivatePark, suspendPark } from '@/services/superadmin'
import RowMenu from '@/components/Superadmin/RowMenu.vue'
import ParkFormPanel from '@/components/Superadmin/ParkFormPanel.vue'
import ImpersonateModal from '@/components/Superadmin/ImpersonateModal.vue'
import ReasonModal from '@/components/Superadmin/ReasonModal.vue'
import AddAdminModal from '@/components/Superadmin/AddAdminModal.vue'
import InviteLinkModal from '@/components/Superadmin/InviteLinkModal.vue'
import DeleteParkModal from '@/components/Superadmin/DeleteParkModal.vue'
import { PARK_STATUS, formatDate, relativeTime, errorText } from '@/components/Superadmin/adminFormat'

const router = useRouter()
const parks = ref([])
const loading = ref(true)
const loadError = ref('')
const search = ref('')
const panel = ref(null)
const modal = ref(null)
const toast = ref('')

const money = (n) => (n == null ? '' : n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' }))

let seq = 0
async function load() {
  const mine = ++seq
  loading.value = true
  loadError.value = ''
  try {
    const rows = await listParks(search.value.trim())
    if (mine === seq) parks.value = rows
  } catch (err) {
    if (mine === seq) loadError.value = errorText(err, 'No se pudieron cargar los parques.')
  } finally {
    if (mine === seq) loading.value = false
  }
}

let timer = null
watch(search, () => { clearTimeout(timer); timer = setTimeout(load, 250) })
onMounted(load)

function menuFor(p) {
  const suspended = p.status === 'SUSPENDED'
  return [
    { key: 'impersonate', label: 'Ver como este parque', icon: 'mdi-account-eye-outline' },
    { key: 'edit', label: 'Editar configuración del parque', icon: 'mdi-pencil-outline' },
    { key: 'admin', label: 'Agregar administrador', icon: 'mdi-account-plus-outline' },
    { key: 'plan', label: 'Renovar o cambiar plan', icon: 'mdi-credit-card-refresh-outline' },
    suspended
      ? { key: 'reactivate', label: 'Reactivar acceso', icon: 'mdi-play-circle-outline' }
      : { key: 'suspend', label: 'Suspender acceso', icon: 'mdi-pause-circle-outline' },
    { key: 'audit', label: 'Ver bitácora de este parque', icon: 'mdi-clipboard-text-clock-outline' },
    { divider: true },
    { key: 'delete', label: 'Eliminar parque', icon: 'mdi-trash-can-outline', danger: true },
  ]
}

function flash(text) {
  toast.value = text
  setTimeout(() => { toast.value = '' }, 2600)
}

async function onAction(key, park) {
  if (key === 'impersonate') modal.value = { type: 'impersonate', park }
  else if (key === 'edit') panel.value = { mode: 'edit', park }
  else if (key === 'admin') modal.value = { type: 'admin', park }
  else if (key === 'suspend') modal.value = { type: 'suspend', park }
  else if (key === 'delete') modal.value = { type: 'delete', park }
  else if (key === 'plan') router.push({ path: '/superadmin/facturacion', query: { search: park.name } })
  else if (key === 'audit') router.push({ path: '/superadmin/bitacora', query: { parkId: park.id } })
  else if (key === 'reactivate') {
    try {
      await reactivatePark(park.id)
      afterAction('Acceso reactivado')
    } catch (err) {
      flash(errorText(err))
    }
  }
}

function onAdminAdded(res) {
  modal.value = { type: 'invite', inviteUrl: res.inviteUrl, expiresAt: res.expiresAt }
  flash('Administrador agregado')
}

function afterAction(text) {
  modal.value = null
  flash(text)
  load()
}

function onPanelDone() {
  const wasEdit = panel.value?.mode === 'edit'
  panel.value = null
  flash(wasEdit ? 'Cambios guardados' : 'Parque creado')
  load()
}
</script>